import { defineStore } from "pinia";
import { request, requestBool } from "@/api-connectors/basicRequests";
import { route } from "@/router";
import { DateTime } from "luxon";

import { useLocationStore } from "@/stores/locations";
import { useSupplierStore } from "@/stores/suppliers";

import { ensureNoOffset, ensureUTC, sameDay, sameMonth } from "@/functions/luxon";
import { combineItems, queryMatches } from "@/functions/others";

import { PaginatedList } from "@/types/store";
import {
    AddInformationCartParams,
    AddInformationItemParams,
    GetListItemsParams,
    IManualSortingData,
    TagAssignParams
} from "@/types/list";
import { InterfaceRecord, PartiallyRequired } from "@/types/helper";
import { DELIVERY_TYPE } from "@/types/enum/order";
import {
    CartData,
    CatalogItem,
    CategoryTag,
    CustomProduct,
    EditList,
    FilterAttributes,
    List,
    ListItem,
    CartListItem,
    Order,
    Reminder,
} from "@/types/supplier";
import { SORT_METHOD } from "@/types/enum/propEnum";

import { RECYCLE_ITEM_SIZE } from "@/assets/variables";
import { nextTick } from "vue";
import { cleanBody, groupRecycleArray } from "@/functions/converter";
import { GroupedList } from "@/types/others";
import { useDebounceFn } from "@vueuse/core";
import { categoryMatches } from "@/functions/piniaFunctions";
import { currentItem } from "@/composables/viewFunctions/useList";

export interface ListFilterAttributes extends FilterAttributes {
    tags: Record<string, boolean>,
    withQuantity: boolean,
    lastListId: string
}

export interface ListStore {
    selectedOrder: Order,
    cartLists: CartData[],
    allOrders: PaginatedList<Order>,
    order: Order,
    orderDetails: {
        [key: string]: PaginatedList<ListItem>
    }
    // cart: PaginatedList<ListItem>,
    lists: PaginatedList<List>,
    listItems: Record<string, PaginatedList<ListItem>>,
    reminders: {
        [key: string]: PaginatedList<Reminder>
    }
    isLoaded: InterfaceRecord<ListStore, boolean, 'isLoaded'>
    recommendedTags: PaginatedList<PartiallyRequired<CategoryTag, 'id'>>,
    searchQuery: string;
    listFilter: ListFilterAttributes;
    lastSkip: number,
    manualSortingsAutoDisplay: boolean,
    manualSortingList: Array<string>;
    locationCache: Record<string, Omit<ListStore, 'locationCache'>>,
    scanList: Record<string, Array<CatalogItem>>,
    isScanHardware: boolean
}

export const useListStore = defineStore('list', {
    state: (): ListStore => {
        return {
            selectedOrder: new Order(),
            cartLists: [],
            order: new Order(),
            allOrders: { data: [], count: 0 },
            orderDetails: {},
            // cart: { data: [], count: 0 },
            lists: { data: [], count: 0 },
            listItems: {},
            reminders: {},
            recommendedTags: { data: [], count: 0 },
            isLoaded: {
                catalog: false
            },
            searchQuery: '',
            listFilter: {
                tags: {},
                category: {},
                withQuantity: false,
                lastListId: '',
                sortDirection: SORT_METHOD.MANUAL
            },
            lastSkip: 0,
            manualSortingsAutoDisplay: false,
            manualSortingList: [],
            locationCache: {},
            scanList: {},
            isScanHardware: false,
        }
    },

    actions: {
        cacheLocation(locationId: string | undefined): void {
            if (!locationId) return
            const cacheValue = JSON.parse(JSON.stringify(this.$state))
            delete cacheValue.locationCache
            this.locationCache[locationId] = cacheValue
        },

        clearStateVariables(): void {
            // const locationStore = useLocationStore()
            // this.cacheLocation(locationStore.activeLocation?.id)
            this.locationCache = {}
            this.cartLists = []
            // this.cart = { data: [], count: 0 }
            this.lists = { data: [], count: 0 }
            this.listItems = {}
            this.isLoaded = {
                catalog: false
            }
            this.manualSortingsAutoDisplay = false
            this.manualSortingList = []
        },

        manualSortOrder(a: string, b: string): number {
            const indexA = this.manualSortingList.findIndex(sortId => sortId === a)
            const indexB = this.manualSortingList.findIndex(sortId => sortId === b)
            if (indexA < 0) return 1
            if (indexB < 0) return -1
            return indexA - indexB
        },

        async getAllLocationLists(locationId: string): Promise<boolean> {
            if (!locationId) return false

            const response = await request<PaginatedList<List>>({
                method: 'GET',
                route: `locations/${locationId}/lists`,
            })

            if (!response) return false

            this.lists = response
            this.isLoaded.lists = true

            return true
        },

        async addList(newList: List): Promise<boolean> {
            const locationId = useLocationStore().activeLocation?.id

            if (!locationId) return false

            const response = await requestBool({
                method: 'POST',
                route: `locations/${locationId}/lists`,
                body: newList
            }, true)

            if (response) await this.getAllLocationLists(locationId)

            return response
        },

        async editList(editedList: EditList): Promise<boolean> {
            const locationId = useLocationStore().activeLocation?.id

            if (!locationId) return false

            const response = await requestBool({
                method: 'PATCH',
                route: `locations/${locationId}/lists/${editedList.id}`,
                body: editedList
            })

            if (response) await this.getAllLocationLists(locationId)

            return response
        },

        async deleteList(listId: string): Promise<boolean> {
            const locationId = useLocationStore().activeLocation?.id

            if (!locationId) return false

            const response = await requestBool({
                method: 'DELETE',
                route: `locations/${locationId}/lists/${listId}`
            }, true)

            if (response) await this.getAllLocationLists(locationId)

            return response
        },

        async getListItems(params: GetListItemsParams): Promise<ListItem[] | undefined> {
            const {
                locationId,
                listId,
                skip,
                take,
                debounce
            } = params

            const manualSort = this.listFilter.sortDirection === SORT_METHOD.MANUAL

            const response = await request<PaginatedList<ListItem>>({
                method: 'GET',
                route: `locations/${locationId}/lists/${listId}/items`,
                query: { take, skip } //searchKey, category,  sortDesc,  sortField,  tags, withQuantity removed because of offline search/filter
            })

            if (!response) return undefined

            let responseData = response.data

            //TODO: Remove this part and reimplement another solution in sortedListItems computed
            if (manualSort) {
                const manualSortItems: Array<ListItem> = []

                this.manualSortingList.forEach((itemId) => {
                    const item = response.data?.find((listItem) => listItem.id === itemId)

                    if (item) manualSortItems.push(item)
                })

                responseData = manualSortItems

                for (const item of response.data) {
                    const checkItem = responseData.find(itemEl => item.id === itemEl.id)

                    if (!checkItem) responseData.push(item)
                }
            }

            for (const itemIndex in responseData) {
                responseData[itemIndex].recycleId = responseData[itemIndex].id

                if (!responseData[itemIndex].item?.name) {
                    responseData.splice(Number(itemIndex), 1)
                }
            }

            response.count = responseData.length

            //*Added so values in list don't jump back and forth when changing the value while data gets loaded from backend
            const writeData = () => {
                if (currentItem.id) {
                    const patchingItemIndex = responseData.findIndex(item => item.id === currentItem.id)
                    if (patchingItemIndex > -1) {
                        console.warn(currentItem.amount)
                        responseData[patchingItemIndex].quantity = currentItem.amount
                    }
                }

                this.listItems[listId] = { data: responseData, count: response.count }

                return this.listItems[listId].data
            }

            if (debounce) {
                useDebounceFn(writeData, 2000)
                return
            }

            writeData()
        },

        async getList(locationId: string, listId: string): Promise<List | undefined> {
            if (!locationId) return

            return await request<List>({
                method: 'GET',
                route: `locations/${locationId}/lists/${listId}`,
            })
        },

        async resetListItems(locationId: string, listId: string): Promise<boolean> {
            return await requestBool({
                method: 'POST',
                route: `locations/${locationId}/lists/${listId}/items/resetQuantities`
            })
        },

        async addListItem(locationId: string, listId: string, supplierItemId: string, quantity: number, targetStock?: number): Promise<boolean> {
            const response = await request<ListItem>({
                method: 'POST',
                route: `locations/${locationId}/lists/${listId}/items`,
                body: { supplierItemId: supplierItemId, quantity: quantity, targetStock: targetStock }
            }, true, 70)

            return Boolean(response)
        },

        async addInformationItem(params: AddInformationItemParams): Promise<boolean> {
            const { locationId, listId, quantity, targetStock, informationId, productId } = params

            return requestBool({
                method: 'POST',
                route: `locations/${locationId}/lists/${listId}/items`,
                body: { productPresentationId: informationId, quantity, targetStock, productId }
            }, true, 70)
        },

        async patchListItem(locationId: string, listId: string, supplierItemId: string, quantity: number, targetStock?: number): Promise<boolean> {
            const response = await requestBool({
                method: 'PATCH',
                route: `locations/${locationId}/lists/${listId}/items/${supplierItemId}`,
                body: { quantity: quantity, targetStock: targetStock ?? undefined }
            }, true)

            return Boolean(response)
        },

        async deleteItem(locationId: string, listId: string, listItemId: string) {
            const response = await requestBool({
                method: 'DELETE',
                route: `locations/${locationId}/lists/${listId}/items/${listItemId}`
            }, true)

            return response
        },

        async deleteItems(locationId: string, listId: string, listItemIds: string[]): Promise<string[]> {
            const successfulResponses: string[] = []

            for (const listItemId of listItemIds) {
                const deleteResponse = await requestBool({
                    method: 'DELETE',
                    route: `locations/${locationId}/lists/${listId}/items/${listItemId}`
                })

                if (deleteResponse) successfulResponses.push(listItemId)
            }

            await this.getManualSortList(listId)

            if (successfulResponses.length > 0) await this.getListItems(new GetListItemsParams({ locationId, listId }))

            return successfulResponses
        },

        async addListToCart(locationId: string, listId: string) {
            const response = await request<unknown[]>({
                method: 'POST',
                route: `locations/${locationId}/cart`,
                body: { listId: listId }
            }, true, 80)

            return response
        },

        async addItemToCart(locationId: string, itemId: string, quantity: number, supplierId?: string, offset?: number): Promise<boolean> {
            const response = await requestBool({
                method: 'POST',
                route: `locations/${locationId}/cart`,
                body: { itemId: itemId, quantity: quantity, supplierId: supplierId }
            }, true, offset ?? 70)

            return Boolean(response)
        },

        async addInformationItemToCart(params: AddInformationCartParams): Promise<boolean> {
            const { locationId, informationId, productId, quantity, supplierId, offset } = params

            const response = await requestBool({
                method: 'POST',
                route: `locations/${locationId}/cart`,
                body: { productId, quantity, supplierId, informationId }
            }, true, offset ?? 70)

            return Boolean(response)
        },

        async addLastOrderToCart(locationId: string, orderSId: string) {
            const response = await request<unknown[]>({
                method: 'POST',
                route: `locations/${locationId}/cart`,
                body: { orderSId: orderSId }
            }, true, 80)

            return response
        },

        async deleteCartItem(locationId: string, itemId: string): Promise<boolean> {
            return requestBool({
                method: 'DELETE',
                route: `locations/${locationId}/cart/${itemId}`
            }, true)
        },

        async deleteCartList(locationId: string, listId: string): Promise<boolean> {
            return requestBool({
                method: 'DELETE',
                route: `locations/${locationId}/cart`,
                body: {
                    listId
                }
            }, true)
        },

        async deleteFullCart(locationId: string, supplierId: string): Promise<boolean> {
            return requestBool({
                method: 'DELETE',
                route: `locations/${locationId}/cart`,
                body: {
                    supplierId
                }
            }, true)
        },

        async deleteNoListItems(locationId: string, supplierId: string): Promise<boolean> {
            return requestBool({
                method: 'DELETE',
                route: `locations/${locationId}/cart`,
                body: {
                    supplierId,
                    noList: true
                }
            }, true)
        },

        async patchCartItem(locationId: string, itemId: string, quantity: number): Promise<boolean> {
            const response = await requestBool({
                method: 'PATCH',
                route: `locations/${locationId}/cart/${itemId}`,
                body: { quantity: quantity }
            })
            return Boolean(response)
        },

        async getShoppingCart(locationId: string): Promise<boolean> {
            if (!locationId) return false
            const response = await request<CartData[]>({
                method: 'GET',
                route: `locations/${locationId}/cart/new`,
            })

            if (!response) return false

            this.cartLists = response

            return true
        },

        async createOrder(locationId: string): Promise<boolean> {
            if (this.order.selectedDeliveryType === DELIVERY_TYPE.PICKUP) {
                if (this.order.pickupDate) this.order.pickupDate = DateTime.fromISO(this.order.pickupDate).toISO() ?? ''
                delete this.order.deliveryDate

                if (this.order.pickupDate) this.order.pickupDate = ensureUTC(this.order.pickupDate)
            }
            if (this.order.selectedDeliveryType === DELIVERY_TYPE.DELIVERY) {
                if (this.order.deliveryDate) this.order.deliveryDate = DateTime.fromISO(this.order.deliveryDate).toISO() ?? ''
                delete this.order.pickupDate
                delete this.order.pickupLocation

                if (this.order.deliveryDate) this.order.deliveryDate = ensureUTC(this.order.deliveryDate)
            }

            return await requestBool({
                method: 'POST',
                route: `locations/${locationId}/orders`,
                body: this.order
            }, true)
        },

        resetOrder(locationId: string): void {
            this.order = new Order()
            this.getShoppingCart(locationId)
        },

        async getOrders(param: {
            locationId: string,
            take?: number,
            skip?: number,
            end?: number
        }): Promise<PaginatedList<Order> | undefined> {
            const { locationId, skip } = param
            let { take, end } = param

            if (!take) take = 50
            if (!end) end = 50
            if (skip) {
                this.lastSkip += take
            }

            const response = await request<PaginatedList<Order>>({
                method: 'GET',
                route: `locations/${locationId}/orders`,
                query: { take: take, skip: skip }
            })

            if (!response) return undefined
            if (skip) {
                for (let i = 0; i < take; i++) {
                    if (!response.data[i]) continue

                    this.allOrders.data[(end - take) + i] = response.data[i]
                }
            } else {
                if (this.allOrders.data?.length) this.allOrders.data = []
                this.$patch({
                    allOrders: response
                })
                this.lastSkip = this.allOrders.data.length
            }

            this.isLoaded.allOrders = true

            return response
        },

        async getAllOrders(locationId: string): Promise<boolean> {
            const response = await request<PaginatedList<Order>>({
                method: 'GET',
                route: `locations/${locationId}/orders`,
                query: { take: 0 }
            })

            if (!response) return false

            this.allOrders = response
            this.isLoaded.allOrders = true

            return true
        },


        async getOrderDetails(locationId: string, orderId: string): Promise<string | undefined> {
            const response = await request<PaginatedList<ListItem>>({
                method: 'GET',
                route: `locations/${locationId}/orders/${orderId}`,
            }, true)

            if (!response) return

            this.orderDetails[orderId] = response
        },

        async addReminder(locationId: string, listId: string, reminder: Reminder): Promise<boolean> {
            reminder.date = ensureUTC(reminder.date)

            const response = await requestBool({
                method: 'POST',
                route: `locations/${locationId}/lists/${listId}/reminder`,
                body: reminder
            }, true)

            return response
        },

        async editReminder(locationId: string, listId: string, reminder: Reminder): Promise<boolean> {
            reminder.date = ensureUTC(reminder.date)

            const response = await requestBool({
                method: 'PATCH',
                route: `locations/${locationId}/lists/${listId}/reminder/${reminder.id}`,
                body: reminder
            }, true)

            return response
        },

        async getReminders(locationId: string, listId: string): Promise<PaginatedList<Reminder> | undefined> {
            const response = await request<PaginatedList<Reminder>>({
                method: 'GET',
                route: `locations/${locationId}/lists/${listId}/reminder`,
                query: { take: 0 }
            }, true)

            if (!response) return undefined

            response.data.forEach(reminder => {
                reminder.date = ensureNoOffset(reminder.date)
            })

            this.reminders[listId] = response

            return response
        },

        async getLocationReminders(locationId: string): Promise<PaginatedList<Reminder> | undefined> {
            const response = await request<PaginatedList<Reminder>>({
                method: 'GET',
                route: `locations/${locationId}/reminders`,
                query: { take: 0, sortField: 'listId' }
            }, true)

            if (!response) return undefined

            this.reminders = {}

            for (const reminder of response.data) {
                reminder.date = ensureNoOffset(reminder.date)
                if (!reminder.listId) continue

                if (this.reminders[reminder.listId]?.data) {
                    const index = this.reminders[reminder.listId].data?.findIndex((existingReminder) => existingReminder.id === reminder.id)
                    if (index >= 0) {
                        this.reminders[reminder.listId].data[index] = reminder
                    } else {
                        this.reminders[reminder.listId].data.push(reminder)
                        this.reminders[reminder.listId].count += 1
                    }
                } else {
                    this.reminders[reminder.listId] = { data: [reminder], count: 1 }
                }
            }
        },

        async refreshReminders(locationId: string) {
            await this.getAllLocationLists(locationId)
            await this.getLocationReminders(locationId)
        },

        async deleteReminder(locationId: string, listId: string, reminderId: string): Promise<boolean> {
            const response = await requestBool({
                method: 'DELETE',
                route: `locations/${locationId}/lists/${listId}/reminder/${reminderId}`,
            }, true)

            if (response && this.reminders[listId]) {
                const index = this.reminders[listId].data?.findIndex((reminder) => reminder.id === reminderId)
                this.reminders[listId].data?.splice(index, 1)
            }

            return response
        },

        async createTag(locationId: string, tag: CategoryTag): Promise<PartiallyRequired<CategoryTag, 'id'> | undefined> {
            return await request<PartiallyRequired<CategoryTag, 'id'>>({
                method: 'POST',
                route: `locations/${locationId}/tags`,
                body: tag
            }, true)
        },

        async getTags(locationId: string): Promise<boolean> {
            const response = await request<PaginatedList<PartiallyRequired<CategoryTag, 'id'>>>({
                method: 'GET',
                route: `locations/${locationId}/tags`
            }, true)

            if (!response) return false

            this.recommendedTags = response
            this.isLoaded.recommendedTags = true

            return true
        },

        async editTag(locationId: string, tag: CategoryTag): Promise<boolean> {
            return await requestBool({
                method: 'PATCH',
                route: `locations/${locationId}/tags/${tag.id}`,
                body: tag
            }, true)
        },

        async deleteTag(locationId: string, tagId: string): Promise<boolean> {
            return await requestBool({
                method: 'DELETE',
                route: `locations/${locationId}/tags/${tagId}`
            }, true)
        },

        async setTag(params: TagAssignParams): Promise<boolean> {
            const { locationId, listId, listItemId, tagId } = params

            return await requestBool({
                method: 'PUT',
                route: `locations/${locationId}/lists/${listId}/items/${listItemId}/tag/${tagId}`
            }, true)
        },

        async unsetTag(params: TagAssignParams): Promise<boolean> {
            const { locationId, listId, listItemId, tagId } = params

            return await requestBool({
                method: 'DELETE',
                route: `locations/${locationId}/lists/${listId}/items/${listItemId}/tag/${tagId}`
            }, true)
        },

        async getListItem(locationId: string, listId: string, listItemId: string): Promise<boolean> {
            const response = await request<ListItem>({
                method: 'GET',
                route: `locations/${locationId}/lists/${listId}/items/${listItemId}`
            })

            if (!response) return false

            const listItemIndex = this.listItems[listId].data?.findIndex(listItem => listItem.id === listItemId)

            if (listItemIndex < 0) return false

            Object.assign(this.listItems[listId].data[listItemIndex], response)

            return true
        },

        async addCustomProduct(locationId: string, listId: string, customProduct: CustomProduct): Promise<boolean> {
            return requestBool({
                method: 'POST',
                route: `locations/${locationId}/lists/${listId}/items/createItem`,
                body: customProduct
            }, true)
        },

        async editCustomProduct(params: {
            locationId: string;
            listId: string;
            listItemId: string;
            body: CustomProduct
        }) {
            const { locationId, listId, listItemId, body } = params

            const cleaned = cleanBody(body, ['quantity', 'targetStock'])
            return requestBool({
                method: 'PATCH',
                route: `locations/${locationId}/lists/${listId}/items/${listItemId}/updateSupplierItem`,
                body: cleaned
            }, true)
        },

        async getManualSortList(listId: string): Promise<void> {
            const locationStore = useLocationStore();
            const locationId = locationStore.activeLocation?.id

            if (!locationId) return

            const response = await request<Array<string>>({
                method: 'GET',
                route: `locations/${locationId}/lists/${listId}/sortOrder`,
            })

            if (!response) return

            this.manualSortingList = response
            this.listFilter.tags = {}
            this.listFilter.category = {}

            // const newListItems: Array<ListItem> = []
            //
            // this.manualSortingList.forEach((itemId) => {
            //     const item = this.listItems[listId].data?.find((listItem) => listItem.id === itemId)
            //
            //     if (item) newListItems.push(item)
            // })
            //
            // if (newListItems.length) this.listItems[listId].data = newListItems
        },

        async addToManualSortingList(listId: string, body: Array<{
            itemId: string,
            position: number
        }>): Promise<void | undefined> {
            const locationStore = useLocationStore();
            const locationId = locationStore.activeLocation?.id

            if (!locationId) return

            await request<Array<string>>({
                method: 'PUT',
                route: `locations/${locationId}/lists/${listId}/sortOrder`,
                body
            })

            await this.getManualSortList(listId)

            window.dispatchEvent(new Event('force-reload-catalog'))
        },

        async addToManualSortingListDirectly(locationId: string, listId: string, body: Array<{
            itemId: string,
            position: number
        }>): Promise<void | undefined> {

            if (!locationId || !listId) return

            await request<Array<string>>({
                method: 'PUT',
                route: `locations/${locationId}/lists/${listId}/sortOrder`,
                body
            })

            await this.getManualSortList(listId)
        },

        async manualSorting(data: IManualSortingData): Promise<Array<ListItem> | void> {
            const { index, listId, selectedProducts } = data
            const isSelected = Object.values(selectedProducts).some((item) => item)

            if (!isSelected) return;

            const selectedListItemIds: Array<string> = []

            for (const prodId in selectedProducts) {
                if (selectedProducts[prodId]) selectedListItemIds.push(prodId)
            }

            const offsetAmount = this.listItems[listId].data.sort((a, b) => this.manualSortOrder(a.id, b.id)).filter((item: ListItem, indexOffset: number) => {
                if (indexOffset >= index) return false

                return selectedListItemIds.includes(item.id)
            }).length

            const movedListItemsBody: Array<{
                itemId: string,
                position: number
            }> = selectedListItemIds.sort(this.manualSortOrder).map((itemId, indexEL) => {
                return {
                    itemId,
                    position: (index - offsetAmount) + indexEL
                }
            })

            await this.addToManualSortingList(listId, movedListItemsBody)
        },

        handleManualSortingsAutoDisplay(val: boolean): void {
            this.manualSortingsAutoDisplay = val
        },

        addScanItem(listId: string, catalogItem: CatalogItem): void {
            if (this.scanList[listId]) {
                const currentItem = this.scanList[listId].find((item) => item.id === catalogItem.id)
                if (currentItem) {
                    const add = currentItem.amountStep ?? 1;
                    if (currentItem.quantity) {
                        if (currentItem.amountMax && currentItem.quantity + add > currentItem.amountMax) return
                        currentItem.quantity += add
                    }
                } else {
                    this.scanList[listId].push(catalogItem)
                }
            } else this.scanList[listId] = [catalogItem]
        },

        clearScanList(listId: string): void {
            this.scanList[listId] = []
        },

        deleteItemFromScanList(listId: string, id: string): void {
            const indexItem = this.scanList[listId].findIndex((item) => item.id === id)

            if (indexItem !== -1) this.scanList[listId].splice(indexItem, 1)
        },

        changeItemQuantity(supplierId: string, id: string, value?: number): void {
            const item = this.scanList[supplierId].find((item) => item.id === id)

            if (item?.quantity) item.quantity = value
        },
    },

    getters: {
        filterListItems(state): (listId: string) => ListItem[] {
            const supplierStore = useSupplierStore();

            return (listId: string): ListItem[] => {
                //TODO MAYBE REMOVE WHEN BUGFIX IN BACKEND FOR NO EMPTY ITEM.ITEM
                if (state.listItems[listId]?.data) {
                    for (const item of state.listItems[listId].data) {
                        if (!item.item?.name) {
                            const index = state.listItems[listId]?.data.indexOf(item)
                            state.listItems[listId]?.data.splice(index, 1)
                        }
                    }

                    state.listItems[listId].count = state.listItems[listId].data.length
                }

                const searchedItems: ListItem[] = []
                if (state.searchQuery && state.listItems[listId]?.data.length) {
                    const searchQuery = state.searchQuery.toLowerCase()

                    for (const item of state.listItems[listId].data) {
                        if (queryMatches(item.item, searchQuery)) searchedItems.push(item)
                    }
                } else Object.assign(searchedItems, state.listItems[listId]?.data ?? [])

                const filteredItems: ListItem[] = []
                if (Object.keys(state.listFilter.tags).find(tagId => state.listFilter.tags[tagId])) {
                    for (const item of searchedItems) {
                        if (!item.item.tags) continue

                        for (const tag of item.item.tags) {
                            if (state.listFilter.tags[tag.id]) {
                                filteredItems.push(item)
                                break
                            }
                        }
                    }
                } else Object.assign(filteredItems, searchedItems)

                let sortedListItemsNew: ListItem[] = []
                if (!this.activeCategoriesIds.length) {
                    sortedListItemsNew = this.listFilter.withQuantity ? filteredItems.filter(item => item.quantity > 0) : filteredItems
                } else {
                    for (const item of filteredItems) {
                        if (categoryMatches(item.item, this.activeCategoriesNames)) sortedListItemsNew.push(item)
                    }

                    sortedListItemsNew = this.listFilter.withQuantity ? sortedListItemsNew.filter(item => item.quantity > 0) : sortedListItemsNew
                }

                const sortValue = listId ? state.listFilter.sortDirection : supplierStore.sortDesc
                const collator = new Intl.Collator('de')

                if (sortValue === SORT_METHOD.ALPH_DESC) {
                    sortedListItemsNew.sort((a: ListItem, b: ListItem) => {
                        return -1 * collator.compare(a.item.name.replace(/[^\p{L}\p{N}]/gu, ''), b.item.name.replace(/[^\p{L}\p{N}]/gu, ''))
                    })

                    sortedListItemsNew.forEach((item, index) => {
                        item.size = RECYCLE_ITEM_SIZE
                        if (!item.id) item.id = String(index)
                    })
                } else if (sortValue === SORT_METHOD.CAT_FILTER) {
                    //sort category filter
                    const sortedListItemsCat: { [key: string]: ListItem[] } = {}
                    const noCategoryItems: ListItem[] = []

                    const internalAlphabeticalSort = sortedListItemsNew.sort((a: ListItem, b: ListItem) => {
                        return collator.compare(a.item.name, b.item.name)
                    })

                    const chosenTags = Object.keys(state.listFilter.tags).filter((i) => state.listFilter.tags[i]);

                    for (const item of internalAlphabeticalSort ?? []) {
                        item.size = RECYCLE_ITEM_SIZE

                        if (item.item?.tags?.length) {
                            const filteredTags = chosenTags.length ? item.item.tags.filter((t) => chosenTags.includes(t.id)) : item.item.tags;
                            for (const tag of filteredTags ?? []) {
                                item.recycleId = item.id + tag.id

                                if (tag.id in sortedListItemsCat) {
                                    sortedListItemsCat[tag.id].push({
                                        ...JSON.parse(JSON.stringify(item)),
                                        tagName: tag.name
                                    })
                                } else {
                                    sortedListItemsCat[tag.id] = [{
                                        ...JSON.parse(JSON.stringify(item)),
                                        tagName: tag.name
                                    }]
                                }
                            }
                        } else {
                            item.recycleId = item.id
                            noCategoryItems.push(JSON.parse(JSON.stringify(item)))
                        }
                    }

                    sortedListItemsNew = combineItems(sortedListItemsCat, noCategoryItems)
                } else if (sortValue === SORT_METHOD.CAT_SUPPLIER) {
                    //filter supplier
                    const sortedListItemsCat: { [key: string]: ListItem[] } = {}
                    const noCategoryItems: ListItem[] = []

                    const internalAlphabeticalSort = sortedListItemsNew.sort((a: ListItem, b: ListItem) => {
                        return collator.compare(a.item.name, b.item.name)
                    })

                    for (const item of internalAlphabeticalSort ?? []) {
                        if (!item.item) continue

                        let catName = undefined

                        if (item.item.itemGroup1) catName = `${item.item.itemGroup1}`
                        if (item.item.itemGroup2) catName = catName + ` / ${item.item.itemGroup2}`

                        item.recycleId = item.id
                        item.size = RECYCLE_ITEM_SIZE

                        if (catName) {
                            item.recycleId = item.recycleId + catName

                            if (catName in sortedListItemsCat) {
                                sortedListItemsCat[catName].push(JSON.parse(JSON.stringify(item)))
                            } else {
                                sortedListItemsCat[catName] = [JSON.parse(JSON.stringify(item))]
                            }
                        } else {
                            noCategoryItems.push(JSON.parse(JSON.stringify(item)))
                        }
                    }

                    sortedListItemsNew = combineItems(sortedListItemsCat, noCategoryItems)
                } else if (sortValue === SORT_METHOD.MANUAL) {
                    const manualSortOrder: Array<ListItem> = []
                    const noSortOrder: Array<ListItem> = []


                    this.manualSortingList.forEach((itemId) => {
                        const item = sortedListItemsNew.find((listItem) => listItem.id === itemId)

                        if (item) manualSortOrder.push(item)
                    })

                    for (const item of sortedListItemsNew) {
                        const checkItem = manualSortOrder.find(itemEl => item.id === itemEl.id)

                        if (!checkItem) noSortOrder.push(item)
                    }

                    sortedListItemsNew = [...manualSortOrder, ...noSortOrder]
                } else if (sortValue === SORT_METHOD.ALPH_ASC) {
                    sortedListItemsNew.sort((a, b): number => {
                        return collator.compare(a.item.name.replace(/[^\p{L}\p{N}]/gu, ''), b.item.name.replace(/[^\p{L}\p{N}]/gu, ''))
                    })

                    sortedListItemsNew.forEach((item, index) => {
                        item.size = RECYCLE_ITEM_SIZE
                        if (!item.id) item.id = String(index)
                    })
                }

                nextTick(() => window.dispatchEvent(new Event('force-reload-catalog')))
                return sortedListItemsNew
            }
        },

        // cartNoList(state) {
        //     return state.cart.data.filter(item => !item.listId)
        // },

        singleOrder(state): (orderId: string) => Order | undefined {
            return (orderId: string): Order | undefined => {
                return state.allOrders.data.find(order => order.id === orderId)
            }
        },

        filterRecommended(state): (tags?: CategoryTag[], filter?: string) => PartiallyRequired<CategoryTag, 'id'>[] {
            return (tags?: CategoryTag[], filter = ''): PartiallyRequired<CategoryTag, 'id'>[] => {
                const filteredTags = state.recommendedTags.data.filter(tag => tag.name.toLowerCase().includes(filter.toLowerCase()))

                return filteredTags.filter(filterTag => {
                    if (!tags) return true
                    for (const tag of tags) {
                        if (tag.id === filterTag.id) return false
                    }
                    return true
                })
            }
        },

        productTags(state): (listId: string, listItemId: string) => PartiallyRequired<CategoryTag, 'id'>[] {
            return (listId: string, listItemId: string): PartiallyRequired<CategoryTag, 'id'>[] => {
                return state.listItems[listId]?.data?.find(item => item.id === listItemId)?.item.tags ?? []
            }
        },

        supplierOrders(state): (supplierId: string) => Order[] {
            return (supplierId: string): Order[] => {
                return state.allOrders.data.filter(order => order.supplierId === supplierId)
            }
        },

        activeFilters(state): string[] {
            const tagIds = Object.keys(state.listFilter.tags)

            const tagNames: string[] = []
            for (const tagId of tagIds) {
                if (state.listFilter.tags[tagId]) {
                    const tag = state.recommendedTags.data.find(tag => tag.id === tagId)
                    if (!tag) continue;
                    tagNames.push(tag.name)
                }
            }

            return tagNames
        },

        activeFiltersObject(state): { name: string; id: string }[] {
            const tagIds = Object.keys(state.listFilter.tags)

            const tagNames: { name: string; id: string }[] = []
            for (const tagId of tagIds) {
                if (state.listFilter.tags[tagId]) {
                    const tag = state.recommendedTags.data.find(tag => tag.id === tagId)
                    if (!tag) continue;
                    tagNames.push({ name: tag.name, id: tag.id })
                }
            }

            return tagNames
        },

        activeFiltersIds(state): string[] {
            const tagIds = Object.keys(state.listFilter.tags)

            const activeTagIds: string[] = []
            for (const tagId of tagIds) {
                if (state.listFilter.tags[tagId]) activeTagIds.push(tagId)
            }

            return activeTagIds
        },

        activeCategoriesIds(state): string[] {
            const categoryIds = Object.keys(state.listFilter.category)

            const activeCategoryIds: string[] = []
            for (const categoryId of categoryIds) {
                if (state.listFilter.category[categoryId]) activeCategoryIds.push(categoryId)
            }

            return activeCategoryIds
        },

        activeCategoriesNames(state): string[] {
            const supplierStore = useSupplierStore()
            const categoryIds = Object.keys(state.listFilter.category)

            const activeCategoryIds: string[] = []
            for (const categoryId of categoryIds) {
                if (state.listFilter.category[categoryId]) activeCategoryIds.push(categoryId)
            }

            return activeCategoryIds.map<string>((categoryId: string): string => {
                return supplierStore.getSupplierCategoryName(categoryId, supplierStore.selectedSupplierId)
            })
        },

        supplierLists(state): (supplierId: string) => List[] {
            return (supplierId: string): List[] => {
                return state.lists.data.filter(list => list.supplierId === supplierId)
            }
        },

        singleList(state): (listId: string) => List | undefined {
            return (listId: string): List | undefined => {
                return state.lists.data.find((list: List) => list.id === listId)
            }
        },

        allMonthOrders(state): (selectedMonth: string, itemsPerRow: number) => (Order | GroupedList<Order>)[] {
            return (selectedMonth: string, itemsPerRow: number): (Order | GroupedList<Order>)[] => {
                const allMonthOrder = state.allOrders.data.filter(order => sameMonth(order.orderedAt, selectedMonth))

                if (!itemsPerRow || itemsPerRow === 1) return allMonthOrder
                return groupRecycleArray(allMonthOrder, itemsPerRow)
            }
        },

        allDayOrders(state): (isoDay: string, itemsPerRow: number) => (Order | GroupedList<Order>)[] {
            return (isoDay: string, itemsPerRow: number): (Order | GroupedList<Order>)[] => {
                const allDayOrder = state.allOrders.data.filter(order => {
                    return sameDay(order.orderedAt ?? '', isoDay)
                })

                if (!itemsPerRow || itemsPerRow === 1) return allDayOrder
                return groupRecycleArray(allDayOrder, itemsPerRow)
            }
        },

        orderAmount(state): number {
            let amount = 0;
            for (const cartList of state.cartLists) {
                amount += cartList.supplier.totalCartItems
            }
            return amount
        },

        listById(state): (listId: string) => List | undefined {
            return (listId: string) => {
                return state.lists.data.find(list => list.id === listId)
            }
        },

        catalogItemInCart(state): (productId: string) => boolean {
            return (productId: string): boolean => {
                for (const cartList of state.cartLists) {
                    for (const itemList of cartList.lists) {
                        if (itemList.cartItems.find(item => item.item.id === productId)) return true
                    }

                    if (cartList.cartItems.find(item => item.item.id === productId)) return true
                }
                return false
            }
        },

        allItems(state): (supplierId: string) => ListItem[] {
            return (supplierId: string): ListItem[] => {
                const cartList = state.cartLists.find(cartList => cartList.supplier.id === supplierId)

                if (!cartList) return []

                const allItems: ListItem[] = []

                for (const list of cartList.lists) {
                    list.cartItems.forEach(item => allItems.push(item))
                }

                cartList.cartItems.forEach(item => allItems.push(item))

                return allItems
            }
        },

        promotionItemInCart(state): (supplierId: string, itemId: string) => CartListItem | undefined {
            return (supplierId: string, itemId: string): CartListItem | undefined => {
                const cartList = state.cartLists.find(cartList => cartList.supplier.id === supplierId)

                if (!cartList) return undefined

                const promoItems: CartListItem | undefined = cartList.cartItems.find(item => item.isPromotion && item.supplierItemId === itemId)

                return promoItems
            }
        },

        promotionProductInCart(state): (supplierId: string, productId: string) => CartListItem | undefined {
            return (supplierId: string, productId: string): CartListItem | undefined => {
                const cartList = state.cartLists.find(cartList => cartList.supplier.id === supplierId)

                if (!cartList) return undefined

                const promoItems: CartListItem | undefined = cartList.cartItems.find(item => item.isPromotion && item.productId === productId || item.isPromotion && item.item.promotion?.productId === productId)

                return promoItems
            }
        },

        listName(state): string {
            const listId = route.value.params.listId as string
            if (!listId) return ''
            return state.lists.data.find(list => list.id === listId)?.name ?? ''
        },

        hasFilter(state): boolean {
            if (state.searchQuery) return true
            if (Object.values(state.listFilter.category).includes(true)) return true
            if (state.listFilter.withQuantity) return true
            for (const tag in state.listFilter.tags) {
                if (state.listFilter.tags[tag]) return true
            }
            return false
        },

        getTagName(state): (tagId: string) => string {
            return (tagId: string): string => {
                return state.recommendedTags.data.find(tag => tag.id === tagId)?.name ?? ''
            }
        },
    }
})

